import React, { useState, useEffect } from 'react';
import {
  FiStar,
  FiCheck,
  FiX,
  FiTrash2,
  FiSearch
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import api from '../../services/api';

const AdminReviews = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchReviews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const fetchReviews = async () => {
    try {
      setLoading(true);
      const params = filter !== 'all' ? { status: filter } : {};
      const data = await api.getAdminReviews(params);
      setReviews(data.reviews || []);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (reviewId, status) => {
    try {
      await api.moderateReview(reviewId, status);
      setReviews(reviews.map(r =>
        r._id === reviewId ? { ...r, status } : r
      ));
      toast.success(`Review ${status}`);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const deleteReview = async (reviewId) => {
    if (!window.confirm('Are you sure you want to delete this review?')) return;

    try {
      await api.deleteReview(reviewId);
      setReviews(reviews.filter(r => r._id !== reviewId));
      toast.success('Review deleted');
    } catch (error) {
      toast.error(error.message);
    }
  };

  const filteredReviews = reviews.filter(r =>
    r.user?.firstName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    r.apartment?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    r.comment?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const renderStars = (rating) => {
    return [...Array(5)].map((_, i) => (
      <FiStar
        key={i}
        size={14}
        fill={i < rating ? '#FFB400' : 'none'}
        color={i < rating ? '#FFB400' : '#ddd'}
      />
    ));
  };

  const getStatusColor = (status) => {
    const colors = {
      approved: { bg: '#E8F5E9', color: '#2E7D32' },
      pending: { bg: '#FFF8E1', color: '#F57C00' },
      rejected: { bg: '#FFEBEE', color: '#C62828' }
    };
    return colors[status] || colors.pending;
  };

  const styles = {
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '24px'
    },
    title: {
      fontSize: '1.5rem',
      fontWeight: '700'
    },
    controls: {
      display: 'flex',
      gap: '16px',
      marginBottom: '24px',
      flexWrap: 'wrap'
    },
    searchWrapper: {
      flex: 1,
      minWidth: '250px',
      position: 'relative'
    },
    searchIcon: {
      position: 'absolute',
      left: '14px',
      top: '50%',
      transform: 'translateY(-50%)',
      color: '#555'
    },
    searchInput: {
      width: '100%',
      padding: '12px 12px 12px 44px',
      border: '1px solid #ddd',
      borderRadius: '8px',
      fontSize: '0.9375rem'
    },
    filters: {
      display: 'flex',
      gap: '8px'
    },
    filterBtn: {
      padding: '10px 16px',
      border: '1px solid #ddd',
      borderRadius: '8px',
      background: 'white',
      cursor: 'pointer',
      fontWeight: '500',
      fontSize: '0.875rem'
    },
    filterActive: {
      background: '#222',
      color: 'white',
      borderColor: '#222'
    },
    reviewsList: {
      display: 'flex',
      flexDirection: 'column',
      gap: '16px'
    },
    reviewCard: {
      background: 'white',
      borderRadius: '12px',
      padding: '20px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
    },
    reviewHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: '12px'
    },
    reviewerInfo: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    },
    avatar: {
      width: '40px',
      height: '40px',
      borderRadius: '50%',
      background: '#f5f5f5',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontWeight: '600',
      color: '#555'
    },
    reviewerName: {
      fontWeight: '500'
    },
    reviewerMeta: {
      color: '#555',
      fontSize: '0.8125rem'
    },
    badge: {
      padding: '4px 10px',
      borderRadius: '20px',
      fontSize: '0.75rem',
      fontWeight: '500'
    },
    apartmentName: {
      fontSize: '0.9375rem',
      fontWeight: '500',
      marginBottom: '8px',
      color: '#1a3a5c'
    },
    stars: {
      display: 'flex',
      gap: '2px',
      marginBottom: '8px'
    },
    comment: {
      color: '#484848',
      lineHeight: '1.6',
      marginBottom: '16px'
    },
    reviewFooter: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingTop: '16px',
      borderTop: '1px solid #f5f5f5'
    },
    reviewDate: {
      color: '#555',
      fontSize: '0.8125rem'
    },
    actions: {
      display: 'flex',
      gap: '8px'
    },
    actionBtn: {
      padding: '8px 16px',
      borderRadius: '6px',
      border: 'none',
      cursor: 'pointer',
      fontSize: '0.8125rem',
      fontWeight: '500',
      display: 'flex',
      alignItems: 'center',
      gap: '4px'
    },
    empty: {
      textAlign: 'center',
      padding: '60px',
      background: 'white',
      borderRadius: '12px',
      color: '#555'
    }
  };

  const filterOptions = ['all', 'pending', 'approved', 'rejected'];

  if (loading) {
    return <div className="loading-container"><div className="spinner" /></div>;
  }

  return (
    <div>
      <header style={styles.header}>
        <h1 style={styles.title}>Reviews</h1>
      </header>

      <div style={styles.controls}>
        <div style={styles.searchWrapper}>
          <FiSearch style={styles.searchIcon} />
          <input
            type="text"
            placeholder="Search reviews..."
            style={styles.searchInput}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div style={styles.filters}>
          {filterOptions.map(f => (
            <button
              key={f}
              style={{
                ...styles.filterBtn,
                ...(filter === f ? styles.filterActive : {})
              }}
              onClick={() => setFilter(f)}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {filteredReviews.length > 0 ? (
        <div style={styles.reviewsList}>
          {filteredReviews.map(review => {
            const statusStyle = getStatusColor(review.status);
            return (
              <div key={review._id} style={styles.reviewCard}>
                <div style={styles.reviewHeader}>
                  <div style={styles.reviewerInfo}>
                    <div style={styles.avatar}>
                      {review.user?.firstName?.charAt(0) || 'G'}
                    </div>
                    <div>
                      <div style={styles.reviewerName}>
                        {review.user?.firstName} {review.user?.lastName}
                      </div>
                      <div style={styles.reviewerMeta}>
                        {review.user?.email}
                      </div>
                    </div>
                  </div>
                  <span
                    style={{
                      ...styles.badge,
                      background: statusStyle.bg,
                      color: statusStyle.color
                    }}
                  >
                    {review.status}
                  </span>
                </div>

                <div style={styles.apartmentName}>
                  {review.apartment?.name}
                </div>

                <div style={styles.stars}>
                  {renderStars(review.rating)}
                </div>

                <p style={styles.comment}>{review.comment}</p>

                <div style={styles.reviewFooter}>
                  <span style={styles.reviewDate}>
                    {new Date(review.createdAt).toLocaleDateString('en-GB', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric'
                    })}
                  </span>
                  <div style={styles.actions}>
                    {review.status === 'pending' && (
                      <>
                        <button
                          style={{
                            ...styles.actionBtn,
                            background: '#E8F5E9',
                            color: '#2E7D32'
                          }}
                          onClick={() => updateStatus(review._id, 'approved')}
                        >
                          <FiCheck /> Approve
                        </button>
                        <button
                          style={{
                            ...styles.actionBtn,
                            background: '#FFEBEE',
                            color: '#C62828'
                          }}
                          onClick={() => updateStatus(review._id, 'rejected')}
                        >
                          <FiX /> Reject
                        </button>
                      </>
                    )}
                    {review.status !== 'pending' && (
                      <button
                        style={{
                          ...styles.actionBtn,
                          background: '#FFF8E1',
                          color: '#F57C00'
                        }}
                        onClick={() => updateStatus(review._id, 'pending')}
                      >
                        Reset to Pending
                      </button>
                    )}
                    <button
                      style={{
                        ...styles.actionBtn,
                        background: '#f5f5f5',
                        color: '#C62828'
                      }}
                      onClick={() => deleteReview(review._id)}
                    >
                      <FiTrash2 />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div style={styles.empty}>
          <FiStar size={48} style={{ marginBottom: '16px', color: '#ddd' }} />
          <p>No reviews found</p>
        </div>
      )}
    </div>
  );
};

export default AdminReviews;
